/**
 * propertyQueryKeys — TanStack Query key factory for the property
 * slice (SCRUM-167 epic).
 *
 * The factory pattern (vs. inlining `['property', 'list', filters]`
 * at every call site) gives the slice three things up front:
 *
 *   1. **Single root key.** `propertyQueryKeys.all` is the
 *      invalidation target for "any property data" — useful from
 *      future mutation hooks (`useCreateProperty`,
 *      `useArchiveProperty`, …) that need to fan invalidation across
 *      every list / detail consumer.
 *
 *   2. **Compile-time typo guard.** A misspelled string literal in
 *      one `queryKey: ['proprty', 'list']` invalidates nothing and
 *      ships silently — the factory's `readonly` tuple types catch
 *      it at the typecheck boundary instead.
 *
 *   3. **Migration cost.** When the BE wiring lands (SCRUM-169+),
 *      the hooks compose against `propertyQueryKeys.list(filters)`
 *      directly; the SCRUM-168 P0 commit only ships the namespace,
 *      not the hooks. No rework on the consumers.
 *
 * The identity slice still inlines its query keys (see
 * `use-users-list.ts`); promoting it to the same factory is a
 * follow-up if/when its invalidation surface grows enough to justify
 * the churn. The property slice starts here because we know up front
 * the surface area is non-trivial (list + detail + unit detail +
 * mutations across CRUD + publish + archive + ownership).
 */

/**
 * The list filters / pagination shape the eventual `useProperties`
 * hook will consume. Kept here (not in `types.ts`) so the factory's
 * `.list(filters)` overload is self-contained — when SCRUM-169
 * lands a richer `PropertyListInput` type, this alias graduates to
 * `PropertyListInput` from `./types.js`.
 */
export interface PropertyListFiltersStub {
  readonly q?: string;
  readonly status?: 'draft' | 'published' | 'archived';
  readonly page?: number;
  readonly perPage?: number;
}

export const propertyQueryKeys = {
  /** Root namespace — invalidates every property query at once. */
  all: ['property'] as const,

  /** Lists — `propertyQueryKeys.list({ q, status, page, perPage })`. */
  lists: () => [...propertyQueryKeys.all, 'list'] as const,
  list: (filters: PropertyListFiltersStub) =>
    [...propertyQueryKeys.lists(), filters] as const,

  /** Details — `propertyQueryKeys.detail(id)`. */
  details: () => [...propertyQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertyQueryKeys.details(), id] as const,

  /** Unit detail under a parent property. */
  unitDetail: (propertyId: string, unitId: string) =>
    [...propertyQueryKeys.detail(propertyId), 'units', unitId] as const,

  /** Current ownership for a property — `propertyQueryKeys.ownership(id)`. */
  ownership: (propertyId: string) =>
    [...propertyQueryKeys.detail(propertyId), 'ownership'] as const,

  /** Ownership history (reverse-chronological) for a property. */
  ownershipHistory: (propertyId: string) =>
    [...propertyQueryKeys.detail(propertyId), 'ownership', 'history'] as const,

  /**
   * Platform-global amenity catalog (SCRUM-141). Not property-scoped — keyed
   * under the slice root, NOT under `detail(id)`, because the catalog is the
   * same list for every property in the org.
   */
  amenitiesCatalog: () => [...propertyQueryKeys.all, 'amenities-catalog'] as const,

  /** Amenities assigned to a specific property (SCRUM-141). */
  propertyAmenities: (propertyId: string) =>
    [...propertyQueryKeys.detail(propertyId), 'amenities'] as const,
} as const;
